import React, { useEffect, useState } from "react";
import { X, ArrowUpRight, ArrowDownRight, ArrowLeftRight, Check } from "lucide-react";

/**
 * Popup per registrare Entrata / Spesa / Switch (trasferimento interno).
 *
 * onSave payload:
 *   • Entrata / Spesa → { type: "in"|"out", accountId, amount, date, note }
 *   • Switch          → { type: "switch", fromAccountId, toAccountId, amount, date, note }
 *
 * Lo Switch sposta l'importo da un conto ad un altro senza modificare il
 * Patrimonio Totale, e viene salvato come UN SOLO movimento nello storico.
 */
export default function MovementDialog({ open, onClose, accounts, onSave, defaultType = "in" }) {
  const [type, setType] = useState(defaultType);
  const [amount, setAmount] = useState("");
  const [accountId, setAccountId] = useState(accounts[0]?.id || "");
  const [fromAccountId, setFromAccountId] = useState(accounts[0]?.id || "");
  const [toAccountId, setToAccountId] = useState(accounts[1]?.id || "");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setType(defaultType);
      setAmount("");
      setAccountId(accounts[0]?.id || "");
      setFromAccountId(accounts[0]?.id || "");
      // Preseleziona un conto di destinazione diverso dall'origine (se possibile).
      const alt = accounts.find((a) => a.id !== (accounts[0]?.id || ""));
      setToAccountId(alt?.id || accounts[1]?.id || "");
      setDate(new Date().toISOString().slice(0, 10));
      setNote("");
      setError("");
    }
  }, [open, defaultType, accounts]);

  if (!open) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");
    const amt = parseInt(amount, 10);
    if (!Number.isFinite(amt) || amt <= 0) {
      setError("Inserisci un importo valido.");
      return;
    }

    let payload;
    if (type === "switch") {
      if (!fromAccountId || !toAccountId) {
        setError("Seleziona i conti di origine e destinazione.");
        return;
      }
      if (fromAccountId === toAccountId) {
        setError("I due conti devono essere diversi.");
        return;
      }
      payload = {
        type: "switch",
        fromAccountId,
        toAccountId,
        amount: amt,
        date: new Date(`${date}T${new Date().toTimeString().slice(0, 8)}`).toISOString(),
        note,
      };
    } else {
      if (!accountId) {
        setError("Seleziona una fonte di denaro.");
        return;
      }
      payload = {
        type,
        amount: amt,
        accountId,
        date: new Date(`${date}T${new Date().toTimeString().slice(0, 8)}`).toISOString(),
        note,
      };
    }

    const ok = onSave(payload);
    if (ok !== false) onClose();
  };

  const title =
    type === "in"
      ? "Registra un'entrata"
      : type === "out"
      ? "Registra una spesa"
      : "Registra un trasferimento";

  const saveBtnClasses =
    type === "in"
      ? "bg-emerald-400/90 text-black hover:bg-emerald-400"
      : type === "out"
      ? "bg-red-400/90 text-black hover:bg-red-400"
      : "bg-sky-400/90 text-black hover:bg-sky-400";

  return (
    <div
      data-testid="movement-dialog"
      className="fixed inset-0 z-[70] flex items-center justify-center px-4 sm:px-6"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      <form
        onSubmit={handleSubmit}
        className="relative w-full max-w-md rounded-2xl bg-zinc-950 border border-white/10 shadow-[0_0_60px_rgba(16,185,129,0.08)] p-6 sm:p-8"
      >
        <button
          data-testid="movement-close"
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/5 transition-colors"
          aria-label="Chiudi"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="mb-6">
          <div className="text-[10px] tracking-[0.32em] uppercase text-emerald-400/80 font-mono mb-2">
            TIME Watch · Movimento
          </div>
          <h3 className="text-xl sm:text-2xl font-light tracking-tight text-white leading-tight">
            {title}
          </h3>
        </div>

        {/* Type toggle */}
        <div className="grid grid-cols-3 gap-2 mb-5 p-1 rounded-xl bg-black/40 border border-white/5">
          <button
            data-testid="movement-type-in"
            type="button"
            onClick={() => setType("in")}
            className={`inline-flex items-center justify-center gap-2 px-2 py-2 rounded-lg text-[10px] sm:text-[11px] tracking-[0.2em] sm:tracking-[0.24em] uppercase font-mono transition-all ${
              type === "in"
                ? "bg-emerald-400/15 text-emerald-300 border border-emerald-400/30"
                : "text-zinc-500 hover:text-zinc-300 border border-transparent"
            }`}
          >
            <ArrowDownRight className="w-3.5 h-3.5" />
            Entrata
          </button>
          <button
            data-testid="movement-type-out"
            type="button"
            onClick={() => setType("out")}
            className={`inline-flex items-center justify-center gap-2 px-2 py-2 rounded-lg text-[10px] sm:text-[11px] tracking-[0.2em] sm:tracking-[0.24em] uppercase font-mono transition-all ${
              type === "out"
                ? "bg-red-400/15 text-red-300 border border-red-400/30"
                : "text-zinc-500 hover:text-zinc-300 border border-transparent"
            }`}
          >
            <ArrowUpRight className="w-3.5 h-3.5" />
            Spesa
          </button>
          <button
            data-testid="movement-type-switch"
            type="button"
            onClick={() => setType("switch")}
            className={`inline-flex items-center justify-center gap-2 px-2 py-2 rounded-lg text-[10px] sm:text-[11px] tracking-[0.2em] sm:tracking-[0.24em] uppercase font-mono transition-all ${
              type === "switch"
                ? "bg-sky-400/15 text-sky-300 border border-sky-400/30"
                : "text-zinc-500 hover:text-zinc-300 border border-transparent"
            }`}
          >
            <ArrowLeftRight className="w-3.5 h-3.5" />
            Switch
          </button>
        </div>

        {/* Amount */}
        <label className="block mb-4">
          <span className="block text-[10px] tracking-[0.24em] uppercase text-zinc-500 font-mono mb-2">
            Importo (€)
          </span>
          <div className="flex items-baseline gap-2 px-3 py-2 rounded-lg bg-black/40 border border-white/10 focus-within:border-emerald-400/40 transition-colors">
            <span className="text-amber-400/70 text-lg font-light">€</span>
            <input
              data-testid="movement-amount"
              type="text"
              inputMode="numeric"
              autoFocus
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^0-9]/g, "").slice(0, 9))}
              placeholder="0"
              className="flex-1 bg-transparent text-lg text-amber-400 font-mono outline-none tabular-nums"
            />
          </div>
        </label>

        {/* Account select(s) */}
        {type === "switch" ? (
          <>
            <label className="block mb-4">
              <span className="block text-[10px] tracking-[0.24em] uppercase text-zinc-500 font-mono mb-2">
                Da (conto di origine)
              </span>
              <select
                data-testid="movement-from-account"
                value={fromAccountId}
                onChange={(e) => setFromAccountId(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg bg-black/40 border border-white/10 text-sm text-white focus:border-sky-400/40 focus:outline-none transition-colors appearance-none"
                style={{
                  backgroundImage:
                    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%2371717a' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E\")",
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 0.75rem center",
                  backgroundSize: "1rem",
                  paddingRight: "2.5rem",
                }}
              >
                {accounts.map((a) => (
                  <option key={a.id} value={a.id} className="bg-zinc-950">
                    {a.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="block mb-4">
              <span className="block text-[10px] tracking-[0.24em] uppercase text-zinc-500 font-mono mb-2">
                A (conto di destinazione)
              </span>
              <select
                data-testid="movement-to-account"
                value={toAccountId}
                onChange={(e) => setToAccountId(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg bg-black/40 border border-white/10 text-sm text-white focus:border-sky-400/40 focus:outline-none transition-colors appearance-none"
                style={{
                  backgroundImage:
                    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%2371717a' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E\")",
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 0.75rem center",
                  backgroundSize: "1rem",
                  paddingRight: "2.5rem",
                }}
              >
                {accounts.map((a) => (
                  <option key={a.id} value={a.id} className="bg-zinc-950">
                    {a.label}
                  </option>
                ))}
              </select>
            </label>
          </>
        ) : (
          <label className="block mb-4">
            <span className="block text-[10px] tracking-[0.24em] uppercase text-zinc-500 font-mono mb-2">
              Fonte di denaro
            </span>
            <select
              data-testid="movement-account"
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg bg-black/40 border border-white/10 text-sm text-white focus:border-emerald-400/40 focus:outline-none transition-colors appearance-none"
              style={{
                backgroundImage:
                  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%2371717a' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E\")",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 0.75rem center",
                backgroundSize: "1rem",
                paddingRight: "2.5rem",
              }}
            >
              {accounts.map((a) => (
                <option key={a.id} value={a.id} className="bg-zinc-950">
                  {a.label}
                </option>
              ))}
            </select>
          </label>
        )}

        {/* Date */}
        <label className="block mb-4">
          <span className="block text-[10px] tracking-[0.24em] uppercase text-zinc-500 font-mono mb-2">
            Data
          </span>
          <input
            data-testid="movement-date"
            type="date"
            value={date}
            max={new Date().toISOString().slice(0, 10)}
            onChange={(e) => setDate(e.target.value)}
            className="w-full px-3 py-2.5 rounded-lg bg-black/40 border border-white/10 text-sm text-white focus:border-emerald-400/40 focus:outline-none transition-colors"
          />
        </label>

        {/* Note */}
        <label className="block mb-5">
          <span className="block text-[10px] tracking-[0.24em] uppercase text-zinc-500 font-mono mb-2">
            Nota (opzionale)
          </span>
          <input
            data-testid="movement-note"
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, 120))}
            placeholder="Es. Stipendio, Spesa, Bolletta…"
            className="w-full px-3 py-2.5 rounded-lg bg-black/40 border border-white/10 text-sm text-white placeholder-white/25 focus:border-emerald-400/40 focus:outline-none transition-colors"
          />
        </label>

        {error && (
          <div
            data-testid="movement-error"
            className="mb-4 text-[12px] text-red-300 bg-red-500/[0.08] border border-red-500/20 rounded-lg px-3 py-2"
          >
            {error}
          </div>
        )}

        <div className="flex items-center justify-end gap-3">
          <button
            data-testid="movement-cancel"
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-[11px] tracking-[0.24em] uppercase text-zinc-500 hover:text-white transition-colors font-mono"
          >
            Annulla
          </button>
          <button
            data-testid="movement-save"
            type="submit"
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[11px] tracking-[0.24em] uppercase font-medium transition-all ${saveBtnClasses}`}
          >
            <Check className="w-3.5 h-3.5" />
            Registra
          </button>
        </div>
      </form>
    </div>
  );
}
